import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Paper, Typography, Divider, Box, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const rowGridTemplate = 'minmax(0, 4fr) minmax(0, 4fr) minmax(0, 3fr) 40px';
const productsSummaryMinHeight = 320;
const productsSummaryMaxHeight = 520;

function ProductsSummary({ items, onDelete, disabled = false }) {
	const { t } = useTranslation();

	return (
		<Paper
			elevation={4}
			sx={{
				p: 2,
				mt: 3,
				mx: 0.5,
				borderRadius: 2,
				display: 'flex',
				flexDirection: 'column',
				minHeight: productsSummaryMinHeight,
				maxHeight: productsSummaryMaxHeight,
				overflow: 'hidden',
			}}
		>
			{/* Title */}
			<Typography variant="heading5" sx={{ mb: 1 }}>
				{t('new_orders:products_summary:title')}
			</Typography>

			{/* Header row */}
			<Divider sx={{ mt: 2 }} />
			<Box sx={{ py: 1 }}>
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: rowGridTemplate,
						columnGap: 1,
						alignItems: 'end',
					}}
				>
					<Typography variant="subtitle2">
						{t('new_orders:products_summary:customer_item_number')}
					</Typography>
					<Typography variant="subtitle2">
						{t('new_orders:products_summary:sku')}
					</Typography>
					<Typography variant="subtitle2" align="right">
						{t('new_orders:products_summary:quantity')}
					</Typography>
					<Box sx={{ pr: 1 }} />
				</Box>
			</Box>
			<Divider />

			{/* Items */}
			<Box
				sx={{
					mt: 1,
					flex: 1,
					minHeight: 0,
					overflowY: 'auto',
					overflowX: 'hidden',
					scrollbarGutter: 'stable',
				}}
			>
				{items.map((item) => (
					<Box
						key={item.value}
						sx={{
							display: 'grid',
							gridTemplateColumns: rowGridTemplate,
							columnGap: 1,
							alignItems: 'center',
							py: 0.5,
						}}
					>
						<Typography variant="body2" sx={{ mb: 0 }}>
							{item.label}
						</Typography>
						<Typography variant="body2" sx={{ mb: 0 }}>
							{item.designNumber}
						</Typography>
						<Typography variant="body2" align="right" sx={{ mb: 0 }}>
							{item.quantity}
						</Typography>
						<Box sx={{ display: 'flex', justifyContent: 'flex-end', pr: 1 }}>
							<IconButton
								size="small"
								disabled={disabled}
								aria-label={t('common:delete')}
								onClick={() => onDelete(item.value)}
							>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</Box>
					</Box>
				))}
			</Box>
		</Paper>
	);
}

ProductsSummary.propTypes = {
	items: PropTypes.array,
	onDelete: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
};

export default ProductsSummary;
